import axiosInstance from '@api/axiosInstance';
import { DynamicTable } from '@Components/General/DynamicTable';
import QuickOptions, { QuickOption } from '@Components/General/QuickOptions';
import { donationTypeMapping, donationTypes } from '@data/general';
import useDialog from '@hooks/useDialog';
import useHeader from '@hooks/useHeader';
import {
    ArrowBack,
    ArrowBackRounded,
    ChevronLeftRounded,
    ChevronRight,
    DeleteOutlineRounded,
    EditRounded,
} from '@mui/icons-material';
import {
    Avatar,
    Box,
    Card,
    CardContent,
    CardHeader,
    Chip,
    Divider,
    Grid,
    IconButton,
    Stack,
    Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import DonationActivity from './DonationActivity';
import { get } from 'lodash';

const paymentsRecivedExampleData = [
    {
        payment_date: '15/01/2024',
        amount: 170,
        payment_method: 'Cash',
        payment_note: 'Partial payment for January',
        transaction_id: 'TXN-001',
    },
    {
        payment_date: '20/01/2024',
        amount: 120,
        payment_method: 'Check',
        payment_note: 'Remainder for January',
        transaction_id: 'TXN-002',
    },
    {
        payment_date: '10/02/2024',
        amount: 250,
        payment_method: 'Online Transfer',
        payment_note: 'Full payment for February',
        transaction_id: 'TXN-003',
    },
    {
        payment_date: '05/12/2024',
        amount: 150,
        payment_method: 'Credit Card',
        payment_note: 'Full payment for December',
        transaction_id: 'TXN-004',
    },
];

const paymentsExampleData = [
    {
        date: '01/01/2024',
        amount: 250,
        paid_amount: 250,
        note: 'Tuition for January',
    },
    {
        date: '01/02/2024',
        amount: 250,
        paid_amount: 250,
        note: 'Tuition for February',
    },
    {
        date: '01/03/2024',
        amount: 250,
        paid_amount: 250,
        note: 'Tuition for March',
    },
    {
        date: '01/04/2024',
        amount: 250,
        paid_amount: 120,
        note: 'Tuition for April',
    },
    {
        date: '01/05/2024',
        amount: 250,
        paid_amount: 0,
        note: 'Tuition for May',
    },
    {
        date: '01/06/2024',
        amount: 250,
        paid_amount: 0,
        note: 'Tuition for June',
    },
    {
        date: '01/07/2024',
        amount: 0,
        paid_amount: 0,
        note: 'No tuition for July',
    },
    {
        date: '01/08/2024',
        amount: 250,
        paid_amount: 0,
        note: 'Tuition for August',
    },
    {
        date: '01/09/2024',
        amount: 250,
        paid_amount: 0,
        note: 'Tuition for September',
    },
    {
        date: '01/10/2024',
        amount: 250,
        paid_amount: 0,
        note: 'Tuition for October',
    },
    {
        date: '01/11/2024',
        amount: 250,
        paid_amount: 0,
        note: 'Tuition for November',
    },
    {
        date: '01/12/2024',
        amount: 150,
        paid_amount: 0,
        note: 'Reduced tuition for December',
    },
];

const PaymentsRecived = ({ payments }) => {
    const { t } = useTranslation();

    const columns = [
        {
            name: t('Date'),
            field: 'payment_date',
            Component: TaableCell,
        },
        {
            name: t('Amount'),
            field: 'amount',
            Component: TaableCell,
        },
        {
            name: t('Payment Method'),
            field: 'payment_method',
            Component: TaableCell,
        },
        {
            name: t('Payment Note'),
            field: 'payment_note',
            Component: TaableCell,
        },
    ];
    return (
        <DynamicTable
            tableHeader={
                <Typography variant="titleLarge">
                    {t('Payments Recived')}
                </Typography>
            }
            columns={columns}
            rows={paymentsRecivedExampleData}
        />
    );
};

const Payments = ({ payments }) => {
    const { t } = useTranslation();

    const columns = [
        {
            name: t('Date'),
            field: 'date',
            Component: TaableCell,
        },
        {
            name: t('Amount'),
            field: 'amount',
            Component: TaableCell,
        },
        {
            name: t('Paid'),
            field: 'paid_amount',
            Component: TaableCell,
        },
        {
            name: t('Note'),
            field: 'note',
            Component: TaableCell,
        },
    ];
    return (
        <DynamicTable
            containerProps={{ sx: { maxHeight: 400, overflow: 'auto' } }}
            tableHeader={
                <Typography variant="titleLarge">{t('Payments')}</Typography>
            }
            columns={columns}
            rows={paymentsExampleData}
        />
    );
};

const DetailItem = ({ label, value }) => {
    return (
        <Stack direction="row" spacing={1}>
            <Typography
                variant="bodyMedium"
                color="GrayText"
                minWidth={60}
                textAlign="end"
            >
                {label}:
            </Typography>
            <Typography variant="bodyMedium">{value}</Typography>
        </Stack>
    );
};

const TaableCell = ({ row, field }) => {
    return <Typography variant="bodyMedium">{get(row, field)}</Typography>;
};

const HeadDetailItem = ({ label, value }) => {
    return (
        <Stack flex={1}>
            <Typography variant="bodySmall" color="GrayText" gutterBottom>
                {label}
            </Typography>
            <Typography>{value}</Typography>
        </Stack>
    );
};

const DonationDetails = () => {
    const { i18n, t } = useTranslation();
    const lang = i18n.language;
    const navigate = useNavigate();
    const { donationId } = useParams({});
    const deleteDialog = useDialog();

    const { data, isLoading } = useQuery({
        queryKey: ['donation', donationId],
        queryFn: () =>
            axiosInstance.get(`/donations/${donationId}`).then(res => res.data),
    });

    const { mutate: deleteDonation } = useMutation({
        mutationFn: () => axiosInstance.delete(`/donations/${donationId}`),
        onSuccess: () => {
            navigate('../');
            deleteDialog.close();
        },
    });

    const donationOptions: QuickOption[] = [
        {
            label: t('Edit'),
            onClick: () => navigate('edit'),
            Icon: EditRounded,
        },
        {
            label: t('Delete'),
            onClick: () => deleteDialog.open({ onDelete: deleteDonation }),
            Icon: DeleteOutlineRounded,
        },
    ];

    useHeader({
        title: `${t('Donation')} ${donationId}`,
        titleSuffix: 'John Doe',
    });

    if (isLoading) return null;

    return (
        <Box display="flex" flexDirection="column" gap={2}>
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
            >
                <IconButton>
                    <ArrowBackRounded />
                </IconButton>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="bodyMedium" color="GrayText">
                        1 of 25
                    </Typography>
                    <IconButton>
                        <ChevronLeftRounded />
                    </IconButton>
                    <IconButton>
                        <ChevronRight />
                    </IconButton>
                </Stack>
            </Box>
            <Card>
                <CardHeader
                    avatar={
                        <Avatar
                            src="http://localhost:5000/src/assets/img/Dev/profile.png"
                            alt="John Doe"
                        />
                    }
                    title={
                        <>
                            John Doe
                            <Typography
                                variant="bodyMedium"
                                display="inline-block"
                                marginInlineStart={1}
                            >
                                / Grade 4 Tuition - 2024
                            </Typography>
                        </>
                    }
                    subheader={
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography>
                                {(data.amount || 999).toCurrencyString(
                                    lang,
                                    data.currency || 'USD',
                                )}
                                {data.donation_type ===
                                    donationTypes.RECURRING &&
                                    data.payments > 1 &&
                                    ` × ${data.payments} ${t('per month')}`}
                            </Typography>
                            {data.donation_type === donationTypes.RECURRING && (
                                <Chip
                                    variant="tonal"
                                    size="small"
                                    color="success"
                                    label={t('Recurring')}
                                />
                            )}
                            <Typography> - {t('per month')}</Typography>
                        </Stack>
                    }
                    action={<QuickOptions options={donationOptions} />}
                />
                <CardContent>
                    <Divider />
                    <Stack
                        direction="row"
                        spacing={2}
                        // justifyContent="space-evenly"
                        marginTop={2}
                    >
                        <HeadDetailItem
                            label={t('Recurrence')}
                            value="Monthly"
                        />
                        <HeadDetailItem label={t('Status')} value="Active" />
                        <HeadDetailItem
                            label={t('Created')}
                            value={data.donation_date.toLocalRegularDateTime()}
                        />
                        <HeadDetailItem
                            label={t('Note')}
                            value={data.donation_note}
                        />
                    </Stack>
                </CardContent>
            </Card>
            <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                    <Stack spacing={2}>
                        <Payments payments={data.donation_payments} />

                        <PaymentsRecived payments={data.donation_payments} />
                    </Stack>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Stack spacing={2}>
                        {/* <Card>
                            <CardHeader
                                title={'Donation Details'}
                                subheader="Grade 4 Tuition - 2024"
                            />
                            <CardContent>
                                <Stack spacing={1}>
                                    <DetailItem
                                        label={t('Created')}
                                        value={data.donation_date.toLocalRegularDateTime()}
                                    />
                                    <DetailItem
                                        label={t('Amount')}
                                        value={(
                                            data.amount || 999
                                        ).toCurrencyString(
                                            lang,
                                            data.currency || 'USD',
                                        )}
                                    />

                                    <DetailItem
                                        label={t('Type')}
                                        value={
                                            donationTypeMapping[
                                                data.donation_type
                                            ]
                                        }
                                    />
                                    <DetailItem
                                        label={t('Status')}
                                        value={data.donation_status}
                                    />
                                    <DetailItem
                                        label={t('Note')}
                                        value={data.donation_note}
                                    />
                                </Stack>
                            </CardContent>
                        </Card> */}
                        <DonationActivity />

                        <Card>
                            <CardHeader title={t('Original Data')} />
                        </Card>
                    </Stack>
                </Grid>
            </Grid>
        </Box>
    );
};

export default DonationDetails;
